import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Phone, Loader2, CheckCircle, AlertCircle, ChevronRight, Shield, AlertTriangle, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_LINK = "https://rzp.io/rzp/t8nstix";

const DISCLAIMERS = [
  {
    id: 'trading_risk',
    title: 'Trading Risk Disclaimer',
    text: 'I understand that trading involves risk and I am fully responsible for my own trading decisions. Monk Algo provides software, tools and educational resources only.'
  },
  {
    id: 'profit_disclaimer',
    title: 'Profit Disclaimer',
    text: 'I understand there will be losing trades, losing days, losing weeks and potentially losing months. Past performance does not guarantee future results.'
  },
  {
    id: 'educational_purpose',
    title: 'Educational Purpose Disclaimer',
    text: 'I understand that Monk Algo does not provide financial advice, investment advice, account management, signals, or guaranteed profits.'
  },
  {
    id: 'setup_responsibility',
    title: 'Setup Responsibility',
    text: 'I agree to read/watch all setup and instructional material before requesting support.'
  },
  {
    id: 'subscription_acknowledgement',
    title: 'Subscription Acknowledgement',
    text: 'I understand this is a recurring subscription (if applicable) and remains active until cancelled.'
  },
  {
    id: 'no_refund',
    title: 'No Refund Policy',
    text: 'I understand and accept that all sales are final and no refunds will be provided except where required by law.'
  },
  {
    id: 'capital_risk',
    title: 'Capital Risk Acknowledgement',
    text: 'I understand I should only trade with capital I can afford to risk.'
  },
  {
    id: 'ea_performance',
    title: 'EA Performance Disclaimer',
    text: 'I understand that market conditions change and no Expert Advisor, algorithm, or trading system can guarantee profits.'
  },
  {
    id: 'independent_decision',
    title: 'Independent Decision Acknowledgement',
    text: 'I accept full responsibility for all trades placed on my account, whether manually or through the EA.'
  },
  {
    id: 'terms_acceptance',
    title: 'Terms Acceptance',
    text: 'I have read, understood, and accept all terms, disclaimers, and risk disclosures.'
  }
];

export function SubscribeModal({ isOpen, onClose }) {
  const [step, setStep] = useState(1); // 1 = form, 2 = disclaimers
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [acceptedDisclaimers, setAcceptedDisclaimers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) return 'First name is required';
    if (!formData.last_name.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    if (!formData.phone.trim()) return 'Phone number is required';
    if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) return 'Please enter a valid 10-digit phone number';
    return null;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setStep(2);
  };

  const handleDisclaimerToggle = (id) => {
    setAcceptedDisclaimers(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const allDisclaimersAccepted = DISCLAIMERS.every(d => acceptedDisclaimers[d.id]);

  const handleFinalSubmit = async () => {
    if (!allDisclaimersAccepted) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          phone: formData.phone.replace(/\D/g, ''),
          disclaimers_accepted: true,
          disclaimers_accepted_at: new Date().toISOString(),
          accepted_disclaimers: Object.keys(acceptedDisclaimers).filter(k => acceptedDisclaimers[k])
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.payment_link || RAZORPAY_LINK, '_blank');
        handleClose();
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      window.open(RAZORPAY_LINK, '_blank');
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({ first_name: '', last_name: '', email: '', phone: '' });
    setAcceptedDisclaimers({});
    setError('');
    onClose();
  };

  const acceptedCount = Object.values(acceptedDisclaimers).filter(Boolean).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 flex items-center justify-center z-[101] p-4 pt-20 md:pt-4"
          >
            <div className={`w-full bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[calc(100vh-6rem)] md:max-h-[90vh] flex flex-col ${step === 2 ? 'max-w-2xl' : 'max-w-md'}`}>
              
              {/* Step 1: Registration Form */}
              {step === 1 && (
                <>
                  {/* Header */}
                  <div className="relative px-6 py-5 border-b border-white/10 bg-gradient-to-r from-[#8B5CF6]/10 to-[#007AFF]/10">
                    <button
                      onClick={handleClose}
                      className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                      data-testid="modal-close-btn"
                    >
                      <X size={16} className="text-white/60" />
                    </button>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-[#8B5CF6] flex items-center justify-center text-white text-xs font-bold">1</div>
                      <span className="text-white/40 text-xs">of 2</span>
                    </div>
                    <h2 className="font-heading font-bold text-xl text-white">Complete Your Details</h2>
                    <p className="font-body text-sm text-white/50 mt-1">Fill in your information to proceed</p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="font-body text-xs text-white/50 mb-1.5 block">First Name</label>
                        <div className="relative">
                          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                          <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            placeholder="John"
                            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-body placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 transition-colors"
                            data-testid="subscribe-first-name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="font-body text-xs text-white/50 mb-1.5 block">Last Name</label>
                        <div className="relative">
                          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                          <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            placeholder="Doe"
                            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-body placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 transition-colors"
                            data-testid="subscribe-last-name"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="font-body text-xs text-white/50 mb-1.5 block">Email Address</label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-body placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 transition-colors"
                          data-testid="subscribe-email"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-body text-xs text-white/50 mb-1.5 block">Phone Number</label>
                      <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="9876543210"
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-body placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 transition-colors"
                          data-testid="subscribe-phone"
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
                        <span className="font-body text-sm text-red-400">{error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] hover:from-[#9B6CF6] hover:to-[#1A8AFF] text-white font-heading font-semibold text-sm py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all"
                      data-testid="subscribe-next-btn"
                    >
                      Continue
                      <ChevronRight size={18} />
                    </button>
                  </form>
                </>
              )}

              {/* Step 2: Disclaimers */}
              {step === 2 && (
                <>
                  {/* Header */}
                  <div className="relative px-6 py-5 border-b border-white/10 bg-gradient-to-r from-[#F59E0B]/10 to-[#EF4444]/10">
                    <button
                      onClick={handleClose}
                      className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                    >
                      <X size={16} className="text-white/60" />
                    </button>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-[#F59E0B] flex items-center justify-center text-white text-xs font-bold">2</div>
                      <span className="text-white/40 text-xs">of 2</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-6 h-6 text-[#F59E0B]" />
                      <div>
                        <h2 className="font-heading font-bold text-xl text-white">Risk Acknowledgement</h2>
                        <p className="font-body text-sm text-white/50">Please read and accept all disclaimers to proceed</p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="px-6 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-white/40 font-body">Accepted</span>
                      <span className="text-xs text-white/60 font-mono">{acceptedCount}/{DISCLAIMERS.length}</span>
                    </div>
                    <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-[#F59E0B] to-[#10B981]"
                        initial={{ width: 0 }}
                        animate={{ width: `${(acceptedCount / DISCLAIMERS.length) * 100}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>

                  {/* Disclaimers List */}
                  <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-3">
                    {DISCLAIMERS.map((disclaimer, index) => (
                      <motion.div
                        key={disclaimer.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => handleDisclaimerToggle(disclaimer.id)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          acceptedDisclaimers[disclaimer.id]
                            ? 'bg-[#10B981]/10 border-[#10B981]/30'
                            : 'bg-white/3 border-white/10 hover:border-white/20'
                        }`}
                        data-testid={`disclaimer-${disclaimer.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                            acceptedDisclaimers[disclaimer.id]
                              ? 'bg-[#10B981] border-[#10B981]'
                              : 'border-white/30'
                          }`}>
                            {acceptedDisclaimers[disclaimer.id] && (
                              <Check size={12} className="text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] text-white/30 font-mono">{String(index + 1).padStart(2, '0')}</span>
                              <h4 className="font-heading font-semibold text-white text-sm">{disclaimer.title}</h4>
                            </div>
                            <p className="font-body text-xs text-white/50 leading-relaxed">{disclaimer.text}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="px-6 pb-6 pt-4 border-t border-white/10 space-y-4">
                    {/* Warning */}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                      <AlertTriangle size={16} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      <p className="font-body text-xs text-[#F59E0B]/80">
                        By proceeding, you acknowledge that you have read, understood, and agreed to all the above disclaimers.
                      </p>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
                        <span className="font-body text-sm text-red-400">{error}</span>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={() => setStep(1)}
                        className="px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 font-heading font-medium text-sm transition-colors"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleFinalSubmit}
                        disabled={!allDisclaimersAccepted || loading}
                        className={`flex-1 py-3.5 rounded-xl flex items-center justify-center gap-2 font-heading font-semibold text-sm transition-all ${
                          allDisclaimersAccepted
                            ? 'bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white'
                            : 'bg-white/5 text-white/30 cursor-not-allowed'
                        }`}
                        data-testid="subscribe-submit-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={18} />
                            Continue to Payment
                          </>
                        )}
                      </button>
                    </div>

                    <p className="font-body text-[10px] text-white/30 text-center">
                      You will be redirected to Razorpay secure payment page
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
